import { apiRequest } from '@/lib/api';

// ============================================================================
// TYPES
// ============================================================================

export interface DraftV2 {
  _id: string;
  draftId: string;
  programData: Record<string, any>;
  programTypes: Array<{
    type: 'hybrid' | 'online' | 'via-zoom' | 'on-campus';
    [key: string]: any;
  }>;
  lastSaved: string;
  status: 'in-progress' | 'ready-to-publish';
  createdAt: string;
  updatedAt: string;
}

export interface DraftListItem {
  _id: string;
  draftId: string;
  title: string;
  status: 'in-progress' | 'ready-to-publish';
  lastSaved: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateDraftPayload {
  programData: Record<string, any>;
  programTypes: Array<any>;
}

export interface DraftsListResponse {
  success: boolean;
  message: string;
  data: {
    drafts: DraftListItem[];
    pagination: {
      currentPage: number;
      totalPages: number;
      totalCount: number;
      hasNextPage: boolean;
      hasPrevPage: boolean;
      limit: number;
    };
  };
}

export interface DraftResponse {
  success: boolean;
  message: string;
  data?: DraftV2;
  draftId?: string;
  _id?: string;
}

export interface PublishResponse {
  success: boolean;
  message: string;
  data?: {
    programId: string;
    customProgramId: string;
    programTypes: string[];
    draftDeleted: boolean;
  };
  errors?: any;
}

export interface GetDraftsParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: 'in-progress' | 'ready-to-publish';
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

// ============================================================================
// API FUNCTIONS
// ============================================================================

/**
 * Create a new draft
 */
export async function createDraftV2(payload: CreateDraftPayload): Promise<DraftResponse> {
  return apiRequest('/api/programs-v2/drafts', {
    method: 'POST',
    body: payload,
  });
}

/**
 * Update an existing draft
 */
export async function updateDraftV2(
  draftId: string,
  payload: CreateDraftPayload
): Promise<DraftResponse> {
  return apiRequest(`/api/programs-v2/drafts/${draftId}`, {
    method: 'PUT',
    body: payload,
  });
}

/**
 * Get a single draft by ID
 */
export async function getDraftV2(draftId: string): Promise<DraftResponse> {
  return apiRequest(`/api/programs-v2/drafts/${draftId}`);
}

/**
 * Get all drafts with pagination and filters
 */
export async function getAllDraftsV2(params: GetDraftsParams = {}): Promise<DraftsListResponse> {
  const queryParams = new URLSearchParams();
  
  if (params.page) queryParams.append('page', params.page.toString());
  if (params.limit) queryParams.append('limit', params.limit.toString());
  if (params.search) queryParams.append('search', params.search);
  if (params.status) queryParams.append('status', params.status);
  if (params.sortBy) queryParams.append('sortBy', params.sortBy);
  if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);
  
  const url = `/api/programs-v2/drafts${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
  return apiRequest(url);
}

/**
 * Delete a draft
 */
export async function deleteDraftV2(draftId: string): Promise<{ success: boolean; message: string }> {
  return apiRequest(`/api/programs-v2/drafts/${draftId}`, {
    method: 'DELETE',
  });
}

/**
 * Publish a draft (creates program and deletes draft)
 */
export async function publishDraftV2(draftId: string): Promise<PublishResponse> {
  return apiRequest(`/api/programs-v2/drafts/${draftId}/publish`, {
    method: 'POST',
  });
}

// ============================================================================
// EXPORT AS SINGLE OBJECT
// ============================================================================

export const draftsV2Api = {
  create: createDraftV2,
  update: updateDraftV2,
  get: getDraftV2,
  getAll: getAllDraftsV2,
  delete: deleteDraftV2,
  publish: publishDraftV2,
};




